import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCustomers } from "../services/customersService";

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const totalItemsRef = useRef(0);

  const loadCustomers = useCallback(
    async ({ page = 1, search = searchValue } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchCustomers({
          page,
          total: totalItemsRef.current,
          search,
        });

        if (response.success) {
          const { items = [], pagination } = response.data || {};
          setCustomers(items);

          if (pagination) {
            const { total_pages, current_page, totalItems: totalItemsValue } = pagination;
            setTotalPages(total_pages || 1);
            setCurrentPage(current_page || page || 1);
            if (typeof totalItemsValue === "number") {
              totalItemsRef.current = totalItemsValue;
              setTotalItems(totalItemsValue);
            }
          }
          setSuccess(null);
        } else {
          setError(response.message || DEFAULT_ERROR_MESSAGE);
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
        setError("Failed to fetch customers. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [searchValue]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage > 0 && newPage <= totalPages) {
        setCurrentPage(newPage);
      }
    },
    [totalPages]
  );

  const handleStatusToggle = useCallback((id) => {
    if (!id) return;
    setCustomers((prev) =>
      prev.map((customer) =>
        customer.id === id || customer._id === id
          ? {
              ...customer,
              status: customer.status === "active" ? "inactive" : "active",
            }
          : customer
      )
    );
    setSuccess("Customer status updated locally.");
  }, []);

  const resetAlerts = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  useEffect(() => {
    loadCustomers({ page: currentPage });
  }, [currentPage, loadCustomers]);

  return {
    customers,
    searchValue,
    setSearchValue,
    currentPage,
    totalPages,
    totalItems,
    loading,
    error,
    success,
    handlePageChange,
    handleStatusToggle,
    resetAlerts,
    setCurrentPage,
  };
};

export default useCustomers;
